import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { Modifier, EditorState } from "draft-js";
import { Map } from "immutable";

import Button from "components/library/Button";
import Checkbox from "components/library/Checkbox";
import Popover from "components/library/Popover";
import TextInput from "components/library/TextInput";
import getActiveEditorState from "editor/utils/getActiveEditorState";
import { updateActiveEditor } from "editor/actions/form";
import { removeCustomBlock } from "editor/actions/blocks";
import {
  KEEP_READING_SHORT_CHAR_LIMIT,
  KEEP_READING_LONG_CHAR_LIMIT,
} from "config/constants/editor";

import shared from "../../../shared.css";
import styles from "./KeepReadingEditor.css";

class KeepReadingEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isActive: this.props.isActive,
      showWordCount: this.props.showWordCount,
      buttonText: this.props.buttonText,
      inputElement: null,
      charLimit: this.props.showWordCount
        ? KEEP_READING_SHORT_CHAR_LIMIT
        : KEEP_READING_LONG_CHAR_LIMIT,
    };
    this.calculateOffset = this._calculateOffset.bind(this);
    this.toggleWordCount = this._toggleWordCount.bind(this);
    this.handleTextChange = this._handleTextChange.bind(this);
    this.handleButtonClick = this._handleButtonClick.bind(this);
    this.handleDeleteClick = this._handleDeleteClick.bind(this);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    this.setState({
      isActive: newProps.isActive,
      buttonText: newProps.buttonText,
      showWordCount: newProps.showWordCount,
      charLimit: newProps.showWordCount
        ? KEEP_READING_SHORT_CHAR_LIMIT
        : KEEP_READING_LONG_CHAR_LIMIT,
    });
  }

  _calculateOffset() {
    if (!this.props.element) {
      return;
    }

    let { top, left, height } = this.props.element.getBoundingClientRect();
    const editorElement = document.querySelector(".keep-reading-editor");
    const editorHeight = editorElement ? editorElement.clientHeight : 244;
    const spacing = parseFloat(shared["scale-3"]) * 10;
    const BUFFER = spacing;

    top += spacing + height;

    // check bottom bound and adjust if necessary
    if (top + spacing + height + editorHeight - window.innerHeight > BUFFER) {
      top = window.innerHeight - (editorHeight + BUFFER);
    }

    return { top, left };
  }

  _toggleWordCount() {
    if (this.state.showWordCount) {
      this.setState({
        showWordCount: false,
        charLimit: KEEP_READING_LONG_CHAR_LIMIT,
      });
    } else {
      if (this.state.buttonText.length >= KEEP_READING_SHORT_CHAR_LIMIT) {
        this.setState({
          buttonText: this.state.buttonText.substring(
            0,
            KEEP_READING_SHORT_CHAR_LIMIT
          ),
        });
      }

      this.setState({
        showWordCount: true,
        charLimit: KEEP_READING_SHORT_CHAR_LIMIT,
      });
    }
  }

  _handleTextChange(e) {
    if (e.target.value.length <= this.state.charLimit) {
      this.setState({ buttonText: e.target.value });
    }
  }

  _handleButtonClick(e) {
    e.preventDefault(); // prevent page refresh in Safari
    this.updateBlockData();
    this.setState({ isActive: false });
    this.props.onModalClose();
  }

  _handleDeleteClick(e) {
    e.preventDefault();
    this.props.removeCustomBlock(this.props.block.getKey());
  }

  updateBlockData() {
    const contentState = this.props.editorState.getCurrentContent();
    const selectionState = this.props.editorState.getSelection();
    const newContentState = Modifier.setBlockData(
      contentState,
      selectionState,
      Map({
        showWordCount: this.state.showWordCount,
        buttonText: this.state.buttonText,
      })
    );
    this.props.updateActiveEditor(
      EditorState.push(
        this.props.editorState,
        newContentState,
        "insert-characters"
      )
    );
  }

  render() {
    // TODO: Add the onClick handler to actually delete the image. Will happen after that action gets written.
    const position = this.calculateOffset();

    return (
      <Popover
        isOpened={this.state.isActive}
        position={position}
        className={classNames("keep-reading-editor", styles.root)}
        closeModal={this.props.onModalClose}
      >
        <form>
          <div className={styles.charCountWrapper}>
            <TextInput
              fullWidth
              inputRef={element => {
                if (!this.state.inputElement) {
                  this.setState({ inputElement: element });
                }
              }}
              label="Button text"
              name="button-text"
              onChange={this.handleTextChange}
              placeholder="Type your button text"
              type="text"
              value={this.state.buttonText}
            />
            <div className={styles.charCount}>
              {`${this.state.buttonText.length}/${this.state.charLimit} characters`}
            </div>
          </div>
          <Checkbox
            label="Show remaining word count"
            onChange={this.toggleWordCount}
            isChecked={this.state.showWordCount}
            className={styles.checkbox}
          />
          <Button fullWidth onClick={this.handleButtonClick} type="secondary">
            Apply
          </Button>
          <Button
            destructive
            fullWidth
            onClick={this.handleDeleteClick}
            type="tertiary"
          >
            Delete button
          </Button>
        </form>
      </Popover>
    );
  }
}

const mapStateToProps = state => {
  return {
    editorState: getActiveEditorState(state),
  };
};

const mapDispatchToProps = {
  updateActiveEditor,
  removeCustomBlock,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(KeepReadingEditor);

KeepReadingEditor.propTypes = {
  block: PropTypes.object.isRequired,
  element: PropTypes.instanceOf(Element),
  onModalClose: PropTypes.func.isRequired,
  showWordCount: PropTypes.bool,
  buttonText: PropTypes.string,
  editorState: PropTypes.object,
  updateActiveEditor: PropTypes.func.isRequired,
  removeCustomBlock: PropTypes.func.isRequired,
};
